import React from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import { Globe2 } from "lucide-react";
import { ResponsiveDataList } from "../../../../../components/responsive";
import type { LifecycleRetreat } from "../../../../../types/retreatLifecycle";
import { formatParishOptionLabel } from "../../../../../utils/parishLabels";
import { SectionShell } from "../../lifecycle/SectionShell";
import { upcomingRowStyles } from "../../lifecycle/manageRetreatColumns";
import { RegistrationOpenCard } from "../cards/RegistrationOpenCard";
import { SECTION_IDS } from "../manageRetreatTypes";
import { dashboardOutlinedButtonSx } from "../dashboardUi";

export function RegistrationOpenSection({
  rows,
  total,
  loading,
  searchValue,
  onSearchChange,
  parishes,
  parishFilter,
  onParishFilter,
  onClearParish,
  dataFetchApi,
  columns,
  isSuperAdmin,
  canEdit,
  onSelectedRowsChange,
  onHistory,
  onDelete,
  onStatusToggle,
  onEditInactiveReason,
  onWaitlistExport,
  onFreezeToggle,
  exporting,
  onExport,
}: {
  rows: LifecycleRetreat[];
  total: number;
  loading: boolean;
  searchValue: string;
  onSearchChange: (v: string) => void;
  parishes: any[];
  parishFilter: string;
  onParishFilter: (value: string) => void;
  onClearParish: () => void;
  dataFetchApi: (...args: any[]) => void;
  columns: any[];
  isSuperAdmin: boolean;
  canEdit: boolean;
  onSelectedRowsChange: (selected: any) => void;
  onHistory: (r: LifecycleRetreat) => void;
  onDelete: (r: LifecycleRetreat) => void;
  onStatusToggle: (r: LifecycleRetreat) => void;
  onEditInactiveReason: (r: LifecycleRetreat) => void;
  onWaitlistExport: (r: LifecycleRetreat) => void;
  onFreezeToggle: (r: LifecycleRetreat) => void;
  exporting: boolean;
  onExport: () => void;
}) {
  return (
    <SectionShell
      id={SECTION_IDS.registrationOpen}
      title="Registration Open"
      subtitle="Public retreat pages currently accepting registrations."
      bgcolor="#e8f3fc"
      icon={<Globe2 size={22} color="#1670bf" />}
      total={total}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      extraToolbar={
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 200, bgcolor: "white" }}>
            <InputLabel>Parish filter</InputLabel>
            <Select
              label="Parish filter"
              value={parishFilter}
              onChange={(e) => onParishFilter(String(e.target.value))}
            >
              <MenuItem value="">All parishes</MenuItem>
              {parishes.map((p) => (
                <MenuItem key={p.id} value={String(p.id)}>
                  {formatParishOptionLabel(p)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="outlined"
            onClick={onClearParish}
            sx={dashboardOutlinedButtonSx}
          >
            View All
          </Button>
        </Stack>
      }
    >
      <ResponsiveDataList
        data={rows}
        total={total}
        loading={loading}
        isServerSide
        sortServer
        countPerPage={10}
        dataFetchApi={dataFetchApi}
        columns={columns}
        conditionalRowStyles={upcomingRowStyles}
        selectableRows
        onSelectedRowsChange={onSelectedRowsChange}
        cardGridColumns={{ xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" }}
        renderCard={(r, ctx) => (
          <RegistrationOpenCard
            r={r}
            isSuperAdmin={isSuperAdmin}
            canEdit={canEdit}
            selected={ctx.selected}
            onSelectChange={ctx.onSelectChange}
            onHistory={onHistory}
            onDelete={onDelete}
            onStatusToggle={onStatusToggle}
            onEditInactiveReason={onEditInactiveReason}
            onWaitlistExport={onWaitlistExport}
            onFreezeToggle={onFreezeToggle}
          />
        )}
      />
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          display: "flex",
          alignItems: "center",
          width: "100%",
          "& .retreatButtons": {
            width: { xs: "100%", sm: "auto" },
            justifyContent: "center",
          },
        }}
      >
        <button
          onClick={onExport}
          className="retreatButtons"
          disabled={exporting}
          type="button"
        >
          <i className="mdi mdi-file-excel" />
          <span>{exporting ? "Exporting…" : "Export Retreat Data"}</span>
        </button>
      </Box>
    </SectionShell>
  );
}
